import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  LOGIN_SUPER_ADMIN, REGISTER_SUPER_ADMIN, MY_SUPER_ADMIN_PROFILE,
  LOGIN_COLLEGE_ADMIN, REGISTER_COLLEGE_ADMIN, MY_ADMIN_PROFILE,
  PENDING_ADMIN_REQUESTS, ALL_COLLEGE_ADMINS, APPROVE_COLLEGE_ADMIN,
  ADMIN_STATS, MONITOR_SNAPSHOT, ADMIN_RECENT_USERS, ADMIN_SOCIAL_FEED,
  ADMIN_DELETE_SOCIAL_POST, ADMIN_FEATURE_GATES, SET_FEATURE_GATE,
  LOGIN_ADMIN_WITH_GOOGLE,
} from '../graphql';
import {
  ShieldCheck, LogOut, RefreshCw, Check, X, Clock, Users, Activity, Server,
  Database, Trash2, AlertTriangle, ChevronDown, ChevronUp, Heart,
  MessageCircle, ExternalLink, Flag, ArrowRight, Cpu, ToggleLeft,
} from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const SUPER_TOKEN_KEY = 'g2u_super_admin_token';
const ADMIN_TOKEN_KEY = 'g2u_admin_token';
const API_ORIGIN = process.env.REACT_APP_API_URL || '';

// ── helpers ──────────────────────────────────────────────────────────────────
const parseDate = (v) => {
  if (!v) return null;
  const n = Number(v);
  const d = new Date(Number.isNaN(n) ? v : n);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (v) => {
  const d = parseDate(v);
  return d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};
const humanizeUptime = (secs) => {
  if (secs == null) return '—';
  const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600), m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${Math.floor(secs % 60)}s`;
};
const errText = (res, err) =>
  (res && res.errors && res.errors.length ? res.errors[0].message : '') || (err ? err.message : '');

const logoutAdmin = () => {
  localStorage.removeItem(SUPER_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  window.location.reload();
};

const POST_TYPE_STYLE = {
  ARTICLE:  'bg-blue-100 text-blue-700',
  VIDEO:    'bg-red-100 text-red-700',
  PROJECT:  'bg-violet-100 text-violet-700',
  THOUGHT:  'bg-amber-100 text-amber-700',
  RESOURCE: 'bg-green-100 text-green-700',
};

// ── shared chrome ────────────────────────────────────────────────────────────
function AdminHeader({ name, onLogout }) {
  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-black text-lg" style={{ background: '#1a73e8' }}>A</div>
          <span className="font-bold text-gray-900">Aura Admin</span>
        </div>
        {onLogout && (
          <div className="flex items-center gap-3">
            {name && <span className="text-sm text-gray-600 hidden sm:inline">{name}</span>}
            <button onClick={onLogout} className="btn-secondary flex items-center gap-1.5 text-sm !py-1.5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function AdminBanner() {
  return (
    <div className="bg-amber-100 border-b border-amber-200 text-amber-800 text-xs sm:text-sm text-center px-4 py-1.5 flex items-center justify-center gap-1.5">
      <AlertTriangle size={13} className="flex-shrink-0" />
      Signed in as admin — log out to use Aura as a student.
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 flex-wrap mb-6">
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
            active === t.key ? 'bg-indigo-600 text-white border-transparent shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:border-indigo-300'}`}>
          {t.icon} {t.label}
        </button>
      ))}
    </div>
  );
}

// ── auth (not logged in) ─────────────────────────────────────────────────────
function AuthCard() {
  const [tab, setTab] = useState('super');
  return (
    <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-10">
      <div className="card !p-0 w-full max-w-md overflow-hidden">
        <div className="p-6 pb-0 text-center">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white mx-auto mb-3" style={{ background: '#1a73e8' }}>
            <ShieldCheck size={22} />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Aura Admin Console</h1>
          <p className="text-sm text-gray-500 mb-4">Sign in to manage the campus platform</p>
          <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-xl p-1">
            {[['super', 'Super Admin'], ['college', 'College Admin']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${tab === k ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6">
          <AdminGoogleSignIn />
          {tab === 'super' ? <SuperAdminAuth /> : <CollegeAdminAuth />}
        </div>
      </div>
    </div>
  );
}

function AdminGoogleSignIn() {
  const [googleLogin] = useMutation(LOGIN_ADMIN_WITH_GOOGLE);
  const [gLoading, setGLoading] = useState(false);
  const [gErr, setGErr] = useState('');

  // GIS credential flow (matches LoginPage.js): onSuccess yields a REAL ID token
  // (credential) — the implicit useGoogleLogin flow returns an access token,
  // which the server's verifyIdToken rejects.
  const handleGoogleCredential = async (credentialResponse) => {
    setGLoading(true);
    setGErr('');
    try {
      const idToken = credentialResponse.credential;
      const { data, errors } = await googleLogin({ variables: { idToken } });
      if (errors?.length) throw new Error(errors[0].message);
      const payload = data?.loginAdminWithGoogle;
      if (!payload?.token) throw new Error('Google sign-in failed.');
      if (payload.kind === 'SUPER_ADMIN') {
        localStorage.setItem(SUPER_TOKEN_KEY, payload.token);
      } else if (payload.kind === 'COLLEGE_ADMIN') {
        localStorage.setItem(ADMIN_TOKEN_KEY, payload.token);
      } else {
        throw new Error('Unrecognised admin account type.');
      }
      window.location.reload();
    } catch (err) {
      setGErr(err.message || 'Google sign-in failed.');
      setGLoading(false);
    }
  };

  return (
    <div className="mb-5">
      <div className="flex justify-center">
        {gLoading ? (
          <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        ) : (
          <GoogleLogin
            onSuccess={handleGoogleCredential}
            onError={() => setGErr('Google sign-in was cancelled or failed.')}
            width="320"
            text="continue_with"
          />
        )}
      </div>
      {gErr && <p className="text-sm text-red-600 text-center mt-2">{gErr}</p>}
      <div className="flex items-center gap-3 mt-4">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-xs text-gray-300 font-medium">or continue with email</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>
    </div>
  );
}

function SuperAdminAuth() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [reg, setReg] = useState({ name: '', email: '', password: '', phone: '' });
  const [showSetup, setShowSetup] = useState(false);
  const [loginErr, setLoginErr] = useState('');
  const [regErr, setRegErr] = useState('');
  const [login, { loading: loggingIn }] = useMutation(LOGIN_SUPER_ADMIN);
  const [register, { loading: registering }] = useMutation(REGISTER_SUPER_ADMIN);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    try {
      const res = await login({ variables: form });
      const token = res.data?.loginSuperAdmin?.token;
      if (token) {
        localStorage.setItem(SUPER_TOKEN_KEY, token);
        window.location.reload();
      } else setLoginErr(errText(res) || 'Login failed');
    } catch (err) { setLoginErr(err.message); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegErr('');
    try {
      const res = await register({ variables: { ...reg, phone: reg.phone || null } });
      const token = res.data?.registerSuperAdmin?.token;
      if (token) {
        localStorage.setItem(SUPER_TOKEN_KEY, token);
        window.location.reload();
      } else setRegErr(errText(res) || 'Registration failed');
    } catch (err) { setRegErr(err.message); }
  };

  const locked = /locked/i.test(regErr);

  return (
    <div>
      <form onSubmit={handleLogin} className="space-y-3">
        <input required type="email" className="input w-full" placeholder="Email"
          value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        <input required type="password" className="input w-full" placeholder="Password"
          value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
        {loginErr && <p className="text-sm text-red-600">{loginErr}</p>}
        <button type="submit" disabled={loggingIn} className="btn-primary w-full">
          {loggingIn ? 'Signing in…' : 'Sign in as Super Admin'}
        </button>
      </form>

      <div className="mt-5 pt-4 border-t border-gray-100">
        <button onClick={() => setShowSetup(s => !s)}
          className="flex items-center justify-between w-full text-sm font-medium text-gray-600 hover:text-indigo-600">
          <span>First-time setup</span>
          {showSetup ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showSetup && (
          <form onSubmit={handleRegister} className="mt-3 space-y-3">
            <p className="text-xs text-gray-400">
              Creates the platform's super admin account. This only works once — registration locks after the first super admin exists.
            </p>
            <input required className="input w-full" placeholder="Full name"
              value={reg.name} onChange={e => setReg(r => ({ ...r, name: e.target.value }))} />
            <input required type="email" className="input w-full" placeholder="Email"
              value={reg.email} onChange={e => setReg(r => ({ ...r, email: e.target.value }))} />
            <input required type="password" className="input w-full" placeholder="Password"
              value={reg.password} onChange={e => setReg(r => ({ ...r, password: e.target.value }))} />
            <input className="input w-full" placeholder="Phone (optional)"
              value={reg.phone} onChange={e => setReg(r => ({ ...r, phone: e.target.value }))} />
            {regErr && (
              <div className={`text-sm rounded-xl px-3 py-2 ${locked ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'text-red-600'}`}>
                {locked ? 'Super admin registration is locked — an account already exists. Use the login form above.' : regErr}
              </div>
            )}
            <button type="submit" disabled={registering} className="btn-secondary w-full">
              {registering ? 'Creating…' : 'Create super admin'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function CollegeAdminAuth() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [reg, setReg] = useState({ name: '', email: '', password: '', college: '', branch: '', designation: '', phone: '' });
  const [loginErr, setLoginErr] = useState('');
  const [regErr, setRegErr] = useState('');
  const [login, { loading: loggingIn }] = useMutation(LOGIN_COLLEGE_ADMIN);
  const [register, { loading: registering }] = useMutation(REGISTER_COLLEGE_ADMIN);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    try {
      const res = await login({ variables: form });
      const token = res.data?.loginCollegeAdmin?.token;
      if (token) {
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        window.location.reload();
      } else setLoginErr(errText(res) || 'Login failed');
    } catch (err) { setLoginErr(err.message); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegErr('');
    try {
      const res = await register({
        variables: {
          name: reg.name, email: reg.email, password: reg.password, college: reg.college,
          branch: reg.branch || null, designation: reg.designation || null,
        },
      });
      const token = res.data?.registerCollegeAdmin?.token;
      if (token) {
        // Store the token anyway — myAdminProfile will show approved=false and the
        // pending card ("Request submitted — a super admin must approve your account.").
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        window.location.reload();
      } else setRegErr(errText(res) || 'Registration failed');
    } catch (err) { setRegErr(err.message); }
  };

  if (mode === 'register') {
    return (
      <form onSubmit={handleRegister} className="space-y-3">
        <p className="text-xs text-gray-400">
          Request college admin access — a super admin must approve your account before you can manage anything.
        </p>
        <input required className="input w-full" placeholder="Full name"
          value={reg.name} onChange={e => setReg(r => ({ ...r, name: e.target.value }))} />
        <input required type="email" className="input w-full" placeholder="Email"
          value={reg.email} onChange={e => setReg(r => ({ ...r, email: e.target.value }))} />
        <input required type="password" className="input w-full" placeholder="Password"
          value={reg.password} onChange={e => setReg(r => ({ ...r, password: e.target.value }))} />
        <input required className="input w-full" placeholder="College"
          value={reg.college} onChange={e => setReg(r => ({ ...r, college: e.target.value }))} />
        <div className="grid grid-cols-2 gap-3">
          <input className="input w-full" placeholder="Branch (optional)"
            value={reg.branch} onChange={e => setReg(r => ({ ...r, branch: e.target.value }))} />
          <input className="input w-full" placeholder="Designation (optional)"
            value={reg.designation} onChange={e => setReg(r => ({ ...r, designation: e.target.value }))} />
        </div>
        <input className="input w-full" placeholder="Phone (optional)"
          value={reg.phone} onChange={e => setReg(r => ({ ...r, phone: e.target.value }))} />
        {regErr && <p className="text-sm text-red-600">{regErr}</p>}
        <button type="submit" disabled={registering} className="btn-primary w-full">
          {registering ? 'Submitting…' : 'Request access'}
        </button>
        <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-gray-500 hover:text-indigo-600">
          Already approved? Sign in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleLogin} className="space-y-3">
      <input required type="email" className="input w-full" placeholder="Email"
        value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      <input required type="password" className="input w-full" placeholder="Password"
        value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
      {loginErr && <p className="text-sm text-red-600">{loginErr}</p>}
      <button type="submit" disabled={loggingIn} className="btn-primary w-full">
        {loggingIn ? 'Signing in…' : 'Sign in as College Admin'}
      </button>
      <button type="button" onClick={() => setMode('register')} className="w-full text-sm text-gray-500 hover:text-indigo-600">
        No account? Request access →
      </button>
    </form>
  );
}

// ── stats grid (shared) ──────────────────────────────────────────────────────
function StatsGrid() {
  const { data, loading, error } = useQuery(ADMIN_STATS);
  const stats = data?.adminStats || [];
  if (loading && !stats.length) return <p className="text-sm text-gray-400">Loading stats…</p>;
  if (error && !stats.length) return <p className="text-sm text-red-600">Failed to load stats: {error.message}</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {stats.map(s => {
        const isPendingHot = s.key === 'pending_admins' && s.total > 0;
        return (
          <div key={s.key} className={`card !p-4 ${isPendingHot ? '!bg-amber-50 !border-amber-300 ring-1 ring-amber-200' : ''}`}>
            <p className={`text-xs font-medium mb-1 ${isPendingHot ? 'text-amber-700' : 'text-gray-400'}`}>{s.label}</p>
            <div className="flex items-end gap-2">
              <span className={`text-2xl font-bold ${isPendingHot ? 'text-amber-700' : 'text-gray-900'}`}>{s.total}</span>
              {s.today > 0 && (
                <span className="text-xs font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full mb-1">+{s.today} today</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── moderation (shared) ──────────────────────────────────────────────────────
function ModerationPanel() {
  const { data, loading, error, refetch } = useQuery(ADMIN_SOCIAL_FEED, {
    variables: { limit: 30 }, fetchPolicy: 'cache-and-network',
  });
  const [removePost] = useMutation(ADMIN_DELETE_SOCIAL_POST);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const posts = data?.adminSocialFeed || [];

  const handleRemove = async (post) => {
    if (!window.confirm(`Remove "${post.title}" by ${post.author?.name || 'unknown'}? This cannot be undone.`)) return;
    setBusyId(post.id); setErr('');
    try {
      const res = await removePost({ variables: { postId: post.id } });
      if (res.errors?.length) setErr(res.errors[0].message);
      await refetch();
    } catch (e) { setErr(e.message); }
    setBusyId(null);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-900 flex items-center gap-2"><Flag size={16} className="text-indigo-500" /> Social feed moderation</h2>
        <span className="text-xs text-gray-400">{posts.length} recent posts</span>
      </div>
      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {loading && !posts.length && <p className="text-sm text-gray-400">Loading feed…</p>}
      {error && !posts.length && <p className="text-sm text-red-600">Failed to load feed: {error.message}</p>}
      {!loading && !posts.length && !error && (
        <div className="card !p-8 text-center text-sm text-gray-400">No posts in the feed.</div>
      )}
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="card !p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${POST_TYPE_STYLE[p.postType] || 'bg-gray-100 text-gray-600'}`}>{p.postType}</span>
                  <span className="text-xs text-gray-400">{fmtDate(p.createdAt)}</span>
                </div>
                <h3 className="font-semibold text-gray-900 truncate">{p.title}</h3>
                {p.body && <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{p.body}</p>}
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-1">
                    <ExternalLink size={11} /> {p.url.length > 60 ? p.url.slice(0, 60) + '…' : p.url}
                  </a>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-2 flex-wrap">
                  <span className="font-medium text-gray-500">{p.author?.name}{p.author?.college ? ` · ${p.author.college}` : ''}</span>
                  <span className="flex items-center gap-1"><Heart size={11} /> {p.likes}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={11} /> {p.comments?.length || 0}</span>
                </div>
              </div>
              <button onClick={() => handleRemove(p)} disabled={busyId === p.id}
                className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-xl flex-shrink-0 disabled:opacity-50 transition-colors">
                <Trash2 size={12} /> {busyId === p.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── super admin: overview ────────────────────────────────────────────────────
function ApprovalBadge({ admin }) {
  if (admin.approved) return <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Approved</span>;
  if (admin.rejectionNote) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Rejected</span>
        <span className="text-xs text-red-500 max-w-[180px] truncate" title={admin.rejectionNote}>{admin.rejectionNote}</span>
      </span>
    );
  }
  return <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Pending</span>;
}

function SuperOverview() {
  const pendingQ = useQuery(PENDING_ADMIN_REQUESTS, { fetchPolicy: 'cache-and-network' });
  const allQ = useQuery(ALL_COLLEGE_ADMINS, { fetchPolicy: 'cache-and-network' });
  const recentQ = useQuery(ADMIN_RECENT_USERS, { variables: { limit: 10 } });
  const [approve] = useMutation(APPROVE_COLLEGE_ADMIN, {
    refetchQueries: [
      { query: PENDING_ADMIN_REQUESTS },
      { query: ALL_COLLEGE_ADMINS },
      { query: ADMIN_STATS },
    ],
  });
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const pending = [...(pendingQ.data?.pendingAdminRequests || [])]
    .sort((a, b) => (parseDate(a.createdAt)?.getTime() || 0) - (parseDate(b.createdAt)?.getTime() || 0)); // oldest first
  const allAdmins = [...(allQ.data?.allCollegeAdmins || [])]
    .sort((a, b) => (parseDate(b.createdAt)?.getTime() || 0) - (parseDate(a.createdAt)?.getTime() || 0)); // newest first
  const recent = recentQ.data?.adminRecentUsers || [];

  const decide = async (admin, approved) => {
    let rejectionNote = null;
    if (!approved) {
      const note = window.prompt(`Reject ${admin.name}? Add an optional note for them:`, '');
      if (note === null) return; // cancelled
      rejectionNote = note.trim() || null;
    }
    setBusyId(admin.id); setErr('');
    try {
      const res = await approve({ variables: { adminId: admin.id, approved, rejectionNote } });
      if (res.errors?.length) setErr(res.errors[0].message);
    } catch (e) { setErr(e.message); }
    setBusyId(null);
  };

  return (
    <div className="space-y-8">
      <StatsGrid />

      {/* Pending approvals FIRST */}
      <section>
        <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
          <Clock size={16} className="text-amber-500" /> Pending approvals
          {pending.length > 0 && <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{pending.length}</span>}
        </h2>
        {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
        {pendingQ.error && <p className="text-sm text-red-600 mb-3">{pendingQ.error.message}</p>}
        {pendingQ.loading && !pending.length
          ? <p className="text-sm text-gray-400">Loading…</p>
          : pending.length === 0
            ? <div className="card !p-6 text-center text-sm text-gray-400">No pending admin requests. All caught up.</div>
            : (
              <div className="space-y-2">
                {pending.map(a => (
                  <div key={a.id} className="card !p-4 !bg-amber-50/50 !border-amber-200 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{a.name} <span className="font-normal text-gray-400 text-sm">· {a.email}</span></p>
                      <p className="text-sm text-gray-600">
                        {a.college}{a.branch ? ` · ${a.branch}` : ''}{a.designation ? ` · ${a.designation}` : ''}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Requested {fmtDate(a.createdAt)}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => decide(a, true)} disabled={busyId === a.id}
                        className="flex items-center gap-1 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-xl disabled:opacity-50 transition-colors">
                        <Check size={14} /> Approve
                      </button>
                      <button onClick={() => decide(a, false)} disabled={busyId === a.id}
                        className="flex items-center gap-1 text-sm font-semibold text-red-700 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-xl disabled:opacity-50 transition-colors">
                        <X size={14} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </section>

      {/* All college admins */}
      <section>
        <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-indigo-500" /> All college admins
        </h2>
        {allQ.error && <p className="text-sm text-red-600 mb-3">{allQ.error.message}</p>}
        {allQ.loading && !allAdmins.length
          ? <p className="text-sm text-gray-400">Loading…</p>
          : allAdmins.length === 0
            ? <div className="card !p-6 text-center text-sm text-gray-400">No college admins yet.</div>
            : (
              <div className="card !p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">College</th>
                      <th className="px-4 py-3 font-medium">Role</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allAdmins.map(a => (
                      <tr key={a.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{a.name}</p>
                          <p className="text-xs text-gray-400">{a.email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{a.college}{a.branch ? ` · ${a.branch}` : ''}</td>
                        <td className="px-4 py-3"><span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{a.role}</span></td>
                        <td className="px-4 py-3"><ApprovalBadge admin={a} /></td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(a.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>

      {/* Recent signups */}
      <section>
        <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
          <Users size={16} className="text-violet-500" /> Recent signups
        </h2>
        {recentQ.error && <p className="text-sm text-red-600 mb-3">{recentQ.error.message}</p>}
        {recentQ.loading && !recent.length
          ? <p className="text-sm text-gray-400">Loading…</p>
          : recent.length === 0
            ? <div className="card !p-6 text-center text-sm text-gray-400">No signups yet.</div>
            : (
              <div className="card !p-0 divide-y divide-gray-50">
                {recent.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.college || 'No college set'}</p>
                    </div>
                    <span className="text-xs text-gray-400 truncate max-w-[45%]">{u.email}</span>
                  </div>
                ))}
              </div>
            )}
      </section>
    </div>
  );
}

// ── super admin: monitoring ──────────────────────────────────────────────────
function MonitoringPanel() {
  const [health, setHealth] = useState({ status: 'checking', latency: null });
  const [lastUpdated, setLastUpdated] = useState(null);

  const checkHealth = useCallback(async () => {
    const t0 = performance.now();
    try {
      const res = await fetch(`${API_ORIGIN}/health`);
      setHealth({ status: res.ok ? 'ok' : 'down', latency: Math.round(performance.now() - t0) });
    } catch {
      setHealth({ status: 'down', latency: Math.round(performance.now() - t0) });
    }
    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    checkHealth();
    const id = setInterval(checkHealth, 30000);
    return () => clearInterval(id);
  }, [checkHealth]);

  const { data, error, refetch, loading } = useQuery(MONITOR_SNAPSHOT, {
    pollInterval: 30000,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => setLastUpdated(new Date()),
  });
  const snap = data?.monitorSnapshot;

  const handleRefresh = () => { refetch(); checkHealth(); };

  const cards = snap ? [
    { icon: <Activity size={15} />, label: 'Uptime', value: humanizeUptime(snap.uptimeSeconds) },
    { icon: <Cpu size={15} />, label: 'Node', value: snap.nodeVersion },
    { icon: <Server size={15} />, label: 'Environment', value: snap.env },
    { icon: <Cpu size={15} />, label: 'Memory', value: `${snap.memoryRssMb} MB RSS · ${snap.heapUsedMb} MB heap` },
    { icon: <Database size={15} />, label: 'Database', value: `${snap.dbName} · ${snap.dbSizeMb} MB` },
  ] : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="card !p-3 flex items-center gap-2.5 !rounded-2xl">
          <span className={`w-2.5 h-2.5 rounded-full ${health.status === 'ok' ? 'bg-green-500' : health.status === 'checking' ? 'bg-gray-300 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-sm font-semibold text-gray-900">
            {health.status === 'ok' ? 'API healthy' : health.status === 'checking' ? 'Checking…' : 'API unreachable'}
          </span>
          {health.latency != null && health.status !== 'checking' && (
            <span className="text-xs text-gray-400">{health.latency} ms</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && <span className="text-xs text-gray-400">Last updated {lastUpdated.toLocaleTimeString('en-IN')}</span>}
          <button onClick={handleRefresh} className="btn-secondary flex items-center gap-1.5 text-sm !py-1.5">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && !snap && <p className="text-sm text-red-600">Failed to load monitor snapshot: {error.message}</p>}
      {!snap && !error && <p className="text-sm text-gray-400">Loading snapshot…</p>}

      {snap && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map(c => (
              <div key={c.label} className="card !p-4">
                <p className="text-xs font-medium text-gray-400 flex items-center gap-1.5 mb-1">{c.icon} {c.label}</p>
                <p className="font-semibold text-gray-900 text-sm break-words">{c.value}</p>
              </div>
            ))}
            <div className="card !p-4">
              <p className="text-xs font-medium text-gray-400 flex items-center gap-1.5 mb-2">
                <ToggleLeft size={15} /> Feature gates
                <span className="text-xs text-gray-400">· {snap.gatedResolvers} resolvers gated</span>
              </p>
              {snap.gatedDomains.length === 0
                ? <p className="text-sm text-gray-500">All domains enabled</p>
                : (
                  <div className="flex flex-wrap gap-1">
                    {snap.gatedDomains.map(d => (
                      <span key={d} className="text-xs font-medium text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">{d}</span>
                    ))}
                  </div>
                )}
            </div>
          </div>
          <p className="text-xs text-gray-400">Server time: {snap.serverTime} · auto-refreshes every 30s</p>
        </>
      )}
    </div>
  );
}

// ── super admin: feature gates ───────────────────────────────────────────────
function FeatureGatesPanel() {
  const { data, loading, error, refetch } = useQuery(ADMIN_FEATURE_GATES, { fetchPolicy: 'cache-and-network' });
  const [setGate] = useMutation(SET_FEATURE_GATE);
  const [busyDomain, setBusyDomain] = useState(null);
  const [err, setErr] = useState('');

  const gates = data?.adminFeatureGates || [];
  const liveCount = gates.filter(g => !g.disabled).length;

  const toggle = async (gate) => {
    setBusyDomain(gate.domain); setErr('');
    try {
      const res = await setGate({ variables: { domain: gate.domain, disabled: !gate.disabled } });
      if (res.errors?.length) setErr(res.errors[0].message);
      await refetch();
    } catch (e) { setErr(e.message); }
    setBusyDomain(null);
  };

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h2 className="font-bold text-gray-900 flex items-center gap-2">
          <ToggleLeft size={16} className="text-indigo-500" /> Feature gates
          {gates.length > 0 && (
            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
              {liveCount} of {gates.length} live
            </span>
          )}
        </h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">Changes apply instantly to all users — no redeploy.</p>

      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {error && !gates.length && <p className="text-sm text-red-600">Failed to load feature gates: {error.message}</p>}
      {loading && !gates.length && <p className="text-sm text-gray-400">Loading feature gates…</p>}
      {!loading && !gates.length && !error && (
        <div className="card !p-8 text-center text-sm text-gray-400">No gateable domains reported by the server.</div>
      )}

      {gates.length > 0 && (
        <div className="card !p-0 divide-y divide-gray-50">
          {gates.map(g => (
            <div key={g.domain} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{g.domain}</p>
              </div>
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.disabled ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                  {g.disabled ? 'Off' : 'Live'}
                </span>
                <button onClick={() => toggle(g)} disabled={busyDomain === g.domain}
                  aria-label={`Turn ${g.domain} ${g.disabled ? 'on' : 'off'}`}
                  className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${g.disabled ? 'bg-gray-300' : 'bg-green-500'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${g.disabled ? '' : 'translate-x-5'}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── super admin view ─────────────────────────────────────────────────────────
function SuperAdminView() {
  const { data } = useQuery(MY_SUPER_ADMIN_PROFILE);
  const [tab, setTab] = useState('overview');
  const name = data?.mySuperAdminProfile?.name;

  return (
    <>
      <AdminHeader name={name ? `${name} · Super Admin` : 'Super Admin'} onLogout={logoutAdmin} />
      <AdminBanner />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <TabBar active={tab} onChange={setTab} tabs={[
          { key: 'overview', label: 'Overview', icon: <Users size={14} /> },
          { key: 'moderation', label: 'Moderation', icon: <Flag size={14} /> },
          { key: 'features', label: 'Features', icon: <ToggleLeft size={14} /> },
          { key: 'monitoring', label: 'Monitoring', icon: <Activity size={14} /> },
        ]} />
        {tab === 'overview' && <SuperOverview />}
        {tab === 'moderation' && <ModerationPanel />}
        {tab === 'features' && <FeatureGatesPanel />}
        {tab === 'monitoring' && <MonitoringPanel />}
      </main>
    </>
  );
}

// ── college admin view ───────────────────────────────────────────────────────
function CollegeAdminView() {
  const { data, loading, error } = useQuery(MY_ADMIN_PROFILE);
  const [tab, setTab] = useState('overview');
  const profile = data?.myAdminProfile;

  return (
    <>
      <AdminHeader
        name={profile ? `${profile.name} · ${profile.college}` : 'College Admin'}
        onLogout={logoutAdmin}
      />
      <AdminBanner />
      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading && !profile && <p className="text-sm text-gray-400">Loading profile…</p>}
        {error && !profile && <p className="text-sm text-red-600">Failed to load your admin profile: {error.message}</p>}

        {profile && profile.approved === false && (
          profile.rejectionNote ? (
            <div className="card !p-8 max-w-xl mx-auto text-center !bg-red-50 !border-red-200">
              <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
                <X size={22} />
              </div>
              <h2 className="text-lg font-bold text-red-800 mb-1">Access request rejected</h2>
              <p className="text-sm text-red-700 mb-3">A super admin declined your college admin request.</p>
              <div className="bg-white/70 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800 text-left">
                <span className="font-semibold">Note from the super admin: </span>{profile.rejectionNote}
              </div>
              <button onClick={logoutAdmin} className="btn-secondary mt-5 inline-flex items-center gap-1.5"><LogOut size={14} /> Logout</button>
            </div>
          ) : (
            <div className="card !p-8 max-w-xl mx-auto text-center !bg-amber-50 !border-amber-200">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3">
                <Clock size={22} />
              </div>
              <h2 className="text-lg font-bold text-amber-800 mb-1">Request submitted — awaiting super admin approval</h2>
              <p className="text-sm text-amber-700">
                A super admin must approve your account before you can manage {profile.college}. Check back later.
              </p>
              <button onClick={logoutAdmin} className="btn-secondary mt-5 inline-flex items-center gap-1.5"><LogOut size={14} /> Logout</button>
            </div>
          )
        )}

        {profile && profile.approved && (
          <>
            <TabBar active={tab} onChange={setTab} tabs={[
              { key: 'overview', label: 'Overview', icon: <Users size={14} /> },
              { key: 'moderation', label: 'Moderation', icon: <Flag size={14} /> },
            ]} />
            {tab === 'overview' && (
              <div className="space-y-6">
                <StatsGrid />
                <a href="/college-admin"
                  className="card !p-5 flex items-center justify-between hover:!border-indigo-300 transition-colors group">
                  <div>
                    <p className="font-semibold text-gray-900">Manage announcements &amp; campus news</p>
                    <p className="text-sm text-gray-500">Post, pin and edit updates for {profile.college}</p>
                  </div>
                  <ArrowRight size={18} className="text-indigo-500 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            )}
            {tab === 'moderation' && <ModerationPanel />}
          </>
        )}
      </main>
    </>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const superToken = localStorage.getItem(SUPER_TOKEN_KEY);
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {superToken
        ? <SuperAdminView />
        : adminToken
          ? <CollegeAdminView />
          : (
            <>
              <AdminHeader />
              <AuthCard />
            </>
          )}
    </div>
  );
}
